import { useState, useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import MusicStaff from '@/components/MusicStaff'
import { useMusicPlayback } from '@/hooks/useMusicPlayback'
import {
  tierForLevel,
  generateInterval,
  generateChoices,
  type Interval,
  type GeneratedInterval,
} from '@/lib/musicTheory'

const DEMO_QUESTIONS = 5

const CORRECT_QUIPS = [
  "Not bad. If you signed up, I'd remember that you got this right.",
  "Lucky guess? Sign up and prove it wasn't.",
  "Rachmaninoff had three hands. That's the only explanation I can think of.",
  "You know, if you had an account, I could track how impressive you are.",
  "I like to say the word bassoon. Bassoon. Anyway, nice job.",
  "Music is just the best. That's not a fact. That's a feeling. But I'm right.",
]

const WRONG_QUIPS = [
  "Maybe you should sign up and get some practice in yourself.",
  "If you signed up for the app, you'd remember how many you got wrong.",
  "That's wrong but at least you didn't write parallel fifths.",
  "Close enough for jazz. But this isn't jazz. So... not close enough.",
  "Sign up and I'll help you get better. I'm very motivational. Obviously.",
  "Your kids would never let you live this down. Good thing they can't see this. Unless you sign up.",
]

function pickRandom<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)]
}

interface Question {
  generated: GeneratedInterval
  choices: Interval[]
}

type DemoMode = 'visual' | 'audio'
type DemoState = 'picking' | 'playing' | 'result' | 'done'

interface DemoGameProps {
  onQuip: (text: string) => void
}

const ALL_EXERCISES = [
  {
    name: 'Reading',
    emoji: '📚',
    exercises: [
      { emoji: '🎤', name: 'Word Decode', description: 'Say the word out loud!', demo: null },
      { emoji: '🔤', name: 'Synonym Match', description: 'Pick the matching word', demo: null },
      { emoji: '🧩', name: 'Synonym Pairs', description: 'Find matching pairs', demo: null },
      { emoji: '✏️', name: 'Spelling', description: 'Hear it, spell it', demo: null },
    ],
  },
  {
    name: 'Music',
    emoji: '🎵',
    exercises: [
      { emoji: '🎼', name: 'Interval Recognition', description: 'Name what you see', demo: 'visual' as DemoMode },
      { emoji: '👂', name: 'Ear Training', description: 'Name what you hear', demo: 'audio' as DemoMode },
      { emoji: '🎶', name: 'Melodic Dictation', description: 'Transcribe a melody', demo: null },
      { emoji: '🎹', name: 'Harmonic Dictation', description: 'Identify chord progressions', demo: null },
    ],
  },
]

export default function DemoGame({ onQuip }: DemoGameProps) {
  const { playInterval, isPlaying } = useMusicPlayback()
  const [mode, setMode] = useState<DemoMode | null>(null)
  const [state, setState] = useState<DemoState>('picking')
  const [questions, setQuestions] = useState<Question[]>([])
  const [currentIndex, setCurrentIndex] = useState(0)
  const [chosenAbbr, setChosenAbbr] = useState<string | null>(null)
  const [feedback, setFeedback] = useState<{ correct: boolean } | null>(null)
  const [hasPlayed, setHasPlayed] = useState(false)
  const [correct, setCorrect] = useState(0)
  const [total, setTotal] = useState(0)
  const usedQuipsRef = useRef<Set<string>>(new Set())

  const currentQuestion = questions[currentIndex] || null

  function freshQuip(pool: string[]): string {
    const unused = pool.filter(q => !usedQuipsRef.current.has(q))
    const pick = unused.length > 0 ? pickRandom(unused) : pickRandom(pool)
    usedQuipsRef.current.add(pick)
    return pick
  }

  const startGame = (m: DemoMode) => {
    const tier = tierForLevel(1)
    const qs: Question[] = []
    for (let i = 0; i < DEMO_QUESTIONS; i++) {
      const intervalType = m === 'audio' ? 'melodic' as const : (Math.random() < 0.5 ? 'melodic' as const : 'harmonic' as const)
      const generated = generateInterval(tier, 'TREBLE', intervalType)
      const choices = generateChoices(generated.interval, tier)
      qs.push({ generated, choices })
    }
    setMode(m)
    setQuestions(qs)
    setCurrentIndex(0)
    setChosenAbbr(null)
    setFeedback(null)
    setHasPlayed(false)
    setCorrect(0)
    setTotal(0)
    setState('playing')
  }

  const handlePlay = async () => {
    if (!currentQuestion) return
    await playInterval(currentQuestion.generated.abc)
    if (!hasPlayed) setHasPlayed(true)
  }

  const handleChoice = (choice: Interval) => {
    if (!currentQuestion || state !== 'playing') return
    const isCorrect = choice.abbreviation === currentQuestion.generated.interval.abbreviation
    setChosenAbbr(choice.abbreviation)
    setFeedback({ correct: isCorrect })
    setState('result')
    setCorrect(c => c + (isCorrect ? 1 : 0))
    setTotal(t => t + 1)
    onQuip(freshQuip(isCorrect ? CORRECT_QUIPS : WRONG_QUIPS))
  }

  const nextQuestion = () => {
    const next = currentIndex + 1
    if (next >= questions.length) {
      setState('done')
    } else {
      setCurrentIndex(next)
      setChosenAbbr(null)
      setFeedback(null)
      setHasPlayed(false)
      setState('playing')
    }
  }

  useEffect(() => {
    if (state === 'result') {
      const timer = setTimeout(nextQuestion, 2500)
      return () => clearTimeout(timer)
    }
  }, [state, currentIndex])

  if (state === 'picking') {
    return (
      <div className="space-y-6">
        <h3 className="text-lg font-bold text-center">Try a game</h3>
        {ALL_EXERCISES.map(cat => (
          <div key={cat.name} className="space-y-3">
            <h4 className="text-sm font-semibold text-muted-foreground flex items-center gap-1.5">
              <span>{cat.emoji}</span> {cat.name}
            </h4>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {cat.exercises.map(ex => (
                ex.demo ? (
                  <button
                    key={ex.name}
                    onClick={() => startGame(ex.demo!)}
                    className="p-3 rounded-xl border-2 border-border hover:border-primary hover:shadow-lg transition text-center space-y-1"
                  >
                    <div className="text-3xl">{ex.emoji}</div>
                    <p className="text-xs font-bold leading-tight">{ex.name}</p>
                    <p className="text-[10px] text-muted-foreground">{ex.description}</p>
                  </button>
                ) : (
                  <Link
                    key={ex.name}
                    to="/signup"
                    className="p-3 rounded-xl border-2 border-border hover:border-primary/50 transition text-center space-y-1 opacity-75 hover:opacity-100"
                  >
                    <div className="text-3xl">{ex.emoji}</div>
                    <p className="text-xs font-bold leading-tight">{ex.name}</p>
                    <p className="text-[10px] text-muted-foreground">Sign up to try</p>
                  </Link>
                )
              ))}
            </div>
          </div>
        ))}
      </div>
    )
  }

  if (state === 'done') {
    const accuracy = total > 0 ? Math.round((correct / total) * 100) : 0
    return (
      <div className="text-center space-y-4">
        <h3 className="text-lg font-bold">{correct}/{total} correct ({accuracy}%)</h3>
        <p className="text-sm text-muted-foreground">
          Sign up to track progress, unlock harder levels, and let your kids play too.
        </p>
        <div className="flex gap-3 justify-center">
          <button
            onClick={() => setState('picking')}
            className="px-4 py-2 border border-border rounded-lg font-medium hover:bg-accent transition text-sm"
          >
            Try Again
          </button>
          <Link
            to="/signup"
            className="px-4 py-2 bg-primary text-primary-foreground rounded-lg font-bold hover:opacity-90 transition text-sm"
          >
            Sign Up Free
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="text-center space-y-4">
      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <button onClick={() => setState('picking')} className="hover:text-foreground transition">&larr; Back</button>
        <span>{currentIndex + 1} of {questions.length} &middot; {correct} correct</span>
      </div>

      <div className="w-full bg-accent rounded-full h-1.5">
        <div
          className="bg-primary h-1.5 rounded-full transition-all duration-300"
          style={{ width: `${((currentIndex + (state === 'result' ? 1 : 0)) / questions.length) * 100}%` }}
        />
      </div>

      {currentQuestion && (
        <>
          {mode === 'visual' && (
            <div className="max-w-[200px] mx-auto bg-white rounded-xl p-3 border border-border">
              <MusicStaff abc={currentQuestion.generated.abc} width={150} />
            </div>
          )}

          {mode === 'audio' && (
            <div className="py-2">
              <button
                onClick={handlePlay}
                disabled={isPlaying}
                className="px-6 py-4 text-3xl border-2 border-border rounded-2xl hover:border-primary hover:bg-accent transition disabled:opacity-50"
              >
                {isPlaying ? '🔊' : '🎵'}
              </button>
              <p className="text-xs text-muted-foreground mt-1">
                {hasPlayed ? 'Tap to hear again' : 'Tap to play'}
              </p>
            </div>
          )}

          {mode === 'visual' && (
            <button
              onClick={handlePlay}
              disabled={isPlaying}
              className="px-3 py-1.5 text-xs border border-border rounded-lg hover:bg-accent transition disabled:opacity-50"
            >
              {isPlaying ? '🔊 Playing...' : '🔊 Hear It'}
            </button>
          )}

          <div className="space-y-2 max-w-xs mx-auto">
            {currentQuestion.choices.map(choice => {
              let cls = 'w-full px-4 py-3 rounded-xl text-sm font-bold transition border-2 '
              if (state === 'result') {
                if (choice.abbreviation === currentQuestion.generated.interval.abbreviation) {
                  cls += 'bg-green-100 border-green-500 text-green-800'
                } else if (choice.abbreviation === chosenAbbr) {
                  cls += 'bg-red-100 border-red-500 text-red-800'
                } else {
                  cls += 'border-border text-muted-foreground opacity-50'
                }
              } else {
                cls += 'border-border hover:border-primary hover:bg-accent'
                if (mode === 'audio' && !hasPlayed) cls += ' opacity-50'
              }
              return (
                <button
                  key={choice.abbreviation}
                  onClick={() => handleChoice(choice)}
                  disabled={state === 'result' || (mode === 'audio' && !hasPlayed)}
                  className={cls}
                >
                  {choice.name} ({choice.abbreviation})
                </button>
              )
            })}
          </div>

          {state === 'result' && feedback && (
            <div className={`text-sm font-semibold ${feedback.correct ? 'text-primary' : 'text-amber-500'}`}>
              {feedback.correct ? '✓ Correct!' : `✗ It was: ${currentQuestion.generated.interval.name}`}
            </div>
          )}
        </>
      )}
    </div>
  )
}
